/**
 * Default landing route resolution.
 *
 * Compassus experiences, including users whose selected clinic has
 * `clinic_type === "compassus"`, land on Visit Notes. Users in the 2026 UI
 * land on Predict. Internal Circadia
 * employees (incl. NPs) otherwise land on the Dashboard Overview (`/overview`)
 * except on home-health facilities, which hide that tab and fall back to the
 * Inbox. Everyone else (facility CIP_users, PCC SSO users, VRNs, home-health
 * clinics) defaults to the Inbox (`/dashboard`). Non-Circadia specialist roles
 * keep their existing surfaces: Billing Practitioners → billing module, Nurse
 * Practitioners → Navigator. Centralised here so every entry point stays in
 * sync.
 */

import { routes } from "../router/mobile/routes";
import { isCompassusExperience } from "./isCompassusUser";
import { isInternalCircadiaUser } from "./subdomain";
import { deriveBillingPermissions } from "./billingPermissions";
import { isUse2026UIEnabled } from "./use2026UI";

export const getDefaultLandingPath = (
  user?: User | null,
  clinic?: { clinic_type?: string | null } | null
): string => {
  // On shared CIP hosts, the selected clinic controls whether the user enters
  // the Compassus experience. Hostname/SSO/group-based Compassus users still
  // match here before clinic data is available.
  if (isCompassusExperience({ user, userClinic: clinic })) {
    return routes.visitNotes.path;
  }

  // Predict is the entry point for the 2026 shell (Home stays reachable from
  // the nav). This check comes after Compassus because that experience always
  // uses its dedicated routes.
  if (isUse2026UIEnabled()) {
    return routes.predict.path;
  }

  // Internal Circadia employees (NPs, BPs, VRNs — anyone on the corporate
  // domain) land on the Dashboard Overview, overriding the role-specific
  // landings below. Home-health facilities hide the Overview tab, so send them
  // to the Inbox there instead of a page they can't navigate back to.
  if (isInternalCircadiaUser(user?.email)) {
    return clinic?.clinic_type === "homehealth"
      ? routes.dashboard.path
      : routes.overview.path;
  }

  // Non-Circadia specialist roles keep their existing dedicated surfaces.
  if (deriveBillingPermissions(user?.groups).isBP) {
    return routes.billingModule.path;
  }
  if (user?.isNursePractitioner) {
    return routes.evaluationAndManagement.path;
  }

  // Everyone else — facility CIP_users, PCC SSO users, VRNs and home-health
  // clinics — defaults to the Inbox.
  return routes.dashboard.path;
};
